import {
  type AgentDetails,
  type Rebate,
  type LicenceDetails,
  LicenceDetailsState,
  States,
  useGetListingAgreementConfiguration,
  useGetAgencyMembers,
  useGetTeamsForAgency,
  useInvalidateUseGetAgencyMembers,
  useInvalidateUseGetTeamsForAgency,
  useInvalidateUseGetUrbanXTeamForAgency,
  useSendInvitationEmail,
  useSoftDeleteAgent,
  useCreateAgent,
  useSetAgentDetails,
} from '@urbanx/agx.agencies.client';
import { useQueryClient } from '@tanstack/react-query';
import { AgxFormTextField, AgxFormDropdown, AgxFormDateField } from '@urbanx/agx.forms';
import { AgentRole } from '@urbanx/agx.auth.client';
import { AgxToast } from '@urbanx/agx-ui-components';
import {
  AgxButton,
  AgxColumn,
  AgxDivider,
  AgxRow,
  AgxSaveChangesButton,
  AgxSelectChip,
  AgxTextArea,
  AgxToggleSwitch,
  AgxTypography,
  Icon_CheckmarkCircleOutlineBig,
  Icon_Arrow,
  SAVE_BUTTON_STATES,
  AgxSpacer,
} from '@urbanx/agx.ui-components2';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AgencyContext } from 'contexts';
import { useSelectedAgency } from 'hooks';
import { FormPrompt } from 'components/FormPrompt';
import Popup from 'components/Popup/Popup';
import Panel from 'components/panel/Panel';
import DeleteConfirmation from 'components/delete-confirmation/DeleteConfirmation';
import LoadingState from 'components/loadingState/LoadingState';
import { type AgxToastState } from 'types/commonTypes';
import { normalizeEmail } from 'helpers/emailHelper';
import LogoUpload from './LogoUpload';
import AgentCrmRegistration from './agent-crm-registration/AgentCrmRegistration';
import { LicenceTypeOptions } from './LicenceTypeOptions';
import { AgentRebates } from './AgentRebates';
import { requiresAgentSaveReload } from './agentSaveWarning';
import { getErrorMessage } from 'helpers/getErrorMessage';
import {
  TeamMemberForm,
  teamMemberDefaults,
  teamMemberRequest,
  canEditLicenseeDetails,
} from './teamMemberFormDefinition';
import './AddOrEditTeamMemberPage.scss';

const AgentRoleOptions = Object.entries(AgentRole).map(([key, value]) => ({
  value,
  label: key.replace(/([a-z])([A-Z])/g, '$1 $2'),
}));
const SUCCESS_MODAL_AUTO_DISMISS_MS = 1600;

const AddOrEditTeamMemberPage = ({ isEdit = false }: { isEdit?: boolean }) => {
  const { agentId } = useParams();
  const { agency, isLoading } = useSelectedAgency();
  const members = useGetAgencyMembers(agency?.id ?? '', !!agency?.id);
  const teams = useGetTeamsForAgency(agency?.id ?? '', !!agency?.id);
  const listing = useGetListingAgreementConfiguration(agency?.id ?? '', !!agency?.id);
  const [agencyContext] = useContext(AgencyContext);
  const selectedAgent = members.data?.find((agent) => agent.id === agentId);
  if (isLoading || !agency || members.isPending || teams.isPending) return <LoadingState />;
  const loadFailed = (!members.data && members.isError) || (!teams.data && teams.isError);
  if (loadFailed || (isEdit && !selectedAgent))
    return (
      <AgxTypography type="form_error" role="alert">
        {loadFailed
          ? 'Unable to load team member details. Reload the page to try again.'
          : 'Team member not found.'}
      </AgxTypography>
    );
  return (
    <TeamMemberEditor
      key={`${agency.id}-${isEdit ? agentId : 'new'}`}
      isEdit={isEdit}
      agencyId={agency.id ?? ''}
      selectedAgent={selectedAgent}
      agencyMembers={members.data ?? []}
      teams={(teams.data ?? []).flatMap((team) =>
        team.id && team.name ? [{ value: team.id, label: team.name }] : []
      )}
      licenceStates={agencyContext?.states ?? []}
      licences={agency.companyDetails?.licences}
      agencyRebates={listing.data ? (listing.data.settings.rebates ?? []) : undefined}
      listingError={listing.isError}
    />
  );
};

const TeamMemberEditor = ({
  isEdit,
  agencyId,
  selectedAgent,
  agencyMembers,
  teams,
  licenceStates,
  licences,
  agencyRebates,
  listingError,
}: {
  isEdit: boolean;
  agencyId: string;
  selectedAgent?: AgentDetails;
  agencyMembers: AgentDetails[];
  teams: { value: string; label: string }[];
  licenceStates: States[];
  licences?: LicenceDetails[];
  agencyRebates?: Rebate[];
  listingError: boolean;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showRebates =
    licences?.some(
      (licence) =>
        licence.state === LicenceDetailsState.QLD || licence.state === LicenceDetailsState.NSW
    ) ?? false;
  // This is the last complete server snapshot, not a second editable draft.
  const [source, setSource] = useState(selectedAgent);
  const saving = useRef(false);
  const observedSource = useRef(selectedAgent);
  useEffect(() => {
    // Save-triggered refetches must not replace the mutation's committed snapshot.
    if (observedSource.current === selectedAgent) return;
    observedSource.current = selectedAgent;
    if (!saving.current) setSource(selectedAgent);
  }, [selectedAgent]);
  const defaults = teamMemberDefaults(source, licenceStates, showRebates);
  const [saveWarning, setSaveWarning] = useState<string>();
  const [forceCheck, setForceCheck] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [emailInviteSent, setEmailInviteSent] = useState(source?.emailInvitationSent ?? false);
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });
  const invalidateMembers = useInvalidateUseGetAgencyMembers();
  const invalidateTeams = useInvalidateUseGetTeamsForAgency();
  const invalidateUrbanXTeam = useInvalidateUseGetUrbanXTeamForAgency();
  const { createAgent } = useCreateAgent();
  const { setAgentDetails } = useSetAgentDetails();
  const form = TeamMemberForm.useForm({
    defaultValues: defaults,
    disableWhileSubmitting: true,
    onSubmit: async ({ parsedValues }) => {
      if (saving.current || saveWarning)
        return { status: 'failed', error: new Error('Reload the latest details before saving.') };
      const creating = !parsedValues.id;
      if (
        creating &&
        agencyMembers.some(
          (member) =>
            !member.softDeleted &&
            normalizeEmail(member.email) === normalizeEmail(parsedValues.email)
        )
      ) {
        return {
          status: 'rejected',
          issues: [
            { path: 'email', code: 'duplicate', message: 'User already exists', source: 'server' },
          ],
        };
      }
      saving.current = true;
      try {
        const details = { ...teamMemberRequest(parsedValues), agencyId };
        let saved: AgentDetails | undefined;
        if (creating) {
          const response = await createAgent({ ...details, id: details.email });
          await Promise.all([
            invalidateMembers(agencyId),
            invalidateTeams(agencyId),
            invalidateUrbanXTeam(agencyId, response?.agentId ?? details.email),
          ]);
          saved = queryClient
            .getQueryData<AgentDetails[]>(['getAgencyMembers', agencyId])
            ?.find((agent) => agent.id === (response?.agentId || details.email));
          if (!saved?.eTag) {
            setSaveWarning(
              'The team member was created, but the latest details could not be loaded. Your draft is preserved. Reload the page before editing this team member.'
            );
            return {
              status: 'failed',
              error: new Error('Created team member snapshot unavailable'),
            };
          }
        } else {
          saved = await setAgentDetails(details);
          if (!saved?.eTag || (details.rebateSettings && !saved.rebateSettings))
            throw new Error('The saved team member details could not be confirmed.');
          await Promise.all([
            invalidateMembers(agencyId),
            invalidateTeams(agencyId),
            invalidateUrbanXTeam(agencyId, details.id),
          ]);
        }
        setSource(saved);
        setSuccessMessage(
          creating
            ? `${parsedValues.firstName} ${parsedValues.lastName} has been added to the team!`
            : 'Changes saved successfully.'
        );
        setShowSuccessModal(true);
        return {
          status: 'accepted',
          baseline: teamMemberDefaults(saved, licenceStates, showRebates),
        };
      } catch (error) {
        const requiresReload = requiresAgentSaveReload(error, creating);
        const message = getErrorMessage(
          error,
          'Unable to confirm all changes were saved.',
          requiresReload
            ? 'Your draft is preserved. Reload the latest details before saving again.'
            : 'Your draft is preserved.'
        );
        if (requiresReload) setSaveWarning(message);
        else updateToastState({ color: 'error', message, open: true });
        return { status: 'failed', error };
      } finally {
        // Query notifications can render after the mutation has finished. Consume the
        // refresh here so that its later render cannot replace the accepted snapshot.
        if (parsedValues.id)
          observedSource.current = queryClient
            .getQueryData<AgentDetails[]>(['getAgencyMembers', agencyId])
            ?.find((agent) => agent.id === parsedValues.id);
        saving.current = false;
      }
    },
  });
  const values = form.values;
  const isExistingMember = isEdit || !!values.id;
  const isSaving = form.phase === 'submitting';
  const canViewLicenseeDetails = canEditLicenseeDetails(values.licenceType, licenceStates);
  const licenceOptions = LicenceTypeOptions.filter(
    (item) => !item.state || licenceStates.some((state) => String(state) === String(item.state))
  ).map((item) => item.option);
  const { sendInvitationEmail, sendInvitationEmailPending } = useSendInvitationEmail({
    onSuccess: async () => {
      setEmailInviteSent(true);
      await invalidateMembers(agencyId);
      updateToastState({ color: 'success', message: 'Email Sent!', open: true });
    },
    onError: () =>
      updateToastState({ color: 'error', message: 'Error sending email invitation', open: true }),
  });
  const { softDeleteAgent, softDeleteAgentPending } = useSoftDeleteAgent({
    onSuccess: async () => {
      setShowDeleteConfirmation(false);
      await Promise.all([
        invalidateMembers(agencyId),
        invalidateTeams(agencyId),
        invalidateUrbanXTeam(agencyId, values.id),
      ]);
      navigate('../');
    },
    onError: () =>
      updateToastState({ color: 'error', message: 'Error saving changes', open: true }),
  });
  const onBack = () => navigate('../team-members');
  const saveState = isSaving
    ? SAVE_BUTTON_STATES.SAVING
    : form.isDirty
      ? SAVE_BUTTON_STATES.UNSAVED
      : form.phase === 'succeeded'
        ? SAVE_BUTTON_STATES.SAVED
        : SAVE_BUTTON_STATES.UNSAVED;
  return (
    <AgxColumn fill gap="spacing-lg">
      <AgxRow fill justifyContent="space-between" alignItems="alignCenter">
        <AgxButton
          id="return-button"
          variant="naked"
          text={`${isExistingMember ? 'Edit' : 'Add'} Team Member`}
          leftIcon={<Icon_Arrow direction="left" height={22} width={22} />}
          onClick={() => (form.isDirty ? setForceCheck(true) : onBack())}
        />
        <AgxRow gap="spacing-lg">
          {isExistingMember && (
            <AgxButton
              id="deleteTeamMemberButton"
              text="Delete team member"
              variant="naked"
              size="medium"
              onClick={() => setShowDeleteConfirmation(true)}
              danger
              disabled={isSaving}
            />
          )}
          <AgxSaveChangesButton
            id="save-team-member-button"
            text="Save changes"
            variant="primary"
            size="medium"
            state={saveState}
            onClick={() => void form.submit()}
            disabled={isSaving || !form.isDirty || !!saveWarning}
          />
        </AgxRow>
      </AgxRow>
      <AgxDivider variant="horizontal" />
      {saveWarning && (
        <AgxTypography type="form_error" role="alert">
          {saveWarning}
        </AgxTypography>
      )}
      <FormPrompt
        hasUnsavedChanges={form.isDirty}
        forceCheck={forceCheck}
        resetForceCheck={setForceCheck}
        onBack={onBack}
      />
      <AgxToast selector="#agxToast" toastState={toastState} />
      <AgxSpacer direction="horizontal" wrap gap="spacing-3xl" fill>
        <TeamMemberForm.Root form={form} className="agx-team-member-form-fields">
          <AgxSpacer className="agx-team-member-profile">
            <Panel title="Team Member Details">
              <TeamMemberForm.CustomField name="photo">
                {(field) => (
                  <LogoUpload
                    logo={field.value}
                    onImageChange={field.onChange}
                    agencyId={agencyId}
                    isDisabled={field.disabled}
                  />
                )}
              </TeamMemberForm.CustomField>
              <AgxRow gap="spacing-3xl" fill>
                <TeamMemberForm.Field
                  name="firstName"
                  Input={AgxFormTextField}
                  type="text"
                  label="First name"
                  hideOptionalText
                  stretch
                />
                <TeamMemberForm.Field
                  name="lastName"
                  Input={AgxFormTextField}
                  type="text"
                  label="Last name"
                  hideOptionalText
                  stretch
                />
              </AgxRow>
              <TeamMemberForm.Field
                name="title"
                Input={AgxFormTextField}
                type="text"
                label="Job Title"
                hideOptionalText
                stretch
              />
              <TeamMemberForm.Field
                name="email"
                Input={AgxFormTextField}
                type="email"
                readonly={isExistingMember}
                label="Email"
                hideOptionalText
                stretch
              />
              <AgxRow gap="spacing-2xl" fill>
                <TeamMemberForm.Field
                  name="phone"
                  Input={AgxFormTextField}
                  type="text"
                  label="Phone"
                  hideOptionalText
                  stretch
                />
                <TeamMemberForm.Field
                  name="role"
                  Input={AgxFormDropdown}
                  options={AgentRoleOptions}
                  label="Role"
                />
              </AgxRow>
              <TeamMemberForm.CustomField name="teams">
                {(field) => (
                  <AgxSelectChip
                    id="selTeam"
                    label="Team"
                    size="medium"
                    type="multi-select"
                    variant="chip"
                    defaultSelection={field.value}
                    disabled={field.disabled}
                    onSelect={(options) => field.onChange(options.map((option) => option.value))}
                    options={teams}
                  />
                )}
              </TeamMemberForm.CustomField>
              <TeamMemberForm.Field
                name="licenceType"
                Input={AgxFormDropdown}
                label="Real Estate Licence"
                hideOptionalText
                options={licenceOptions}
              />
              {canViewLicenseeDetails && (
                <TeamMemberForm.CustomField name="useLicenseeDetails">
                  {(field) => (
                    <AgxRow gap="spacing-lg" justifyContent="center">
                      <AgxToggleSwitch
                        id="switchLicenseeDetails"
                        checked={field.value}
                        disabled={field.disabled}
                        onChange={field.onChange}
                      />
                      <AgxTypography
                        htmlFor="switchLicenseeDetails"
                        id="lbllicenseeDetails"
                        type="label"
                        size="medium"
                      >
                        Licensee Details
                      </AgxTypography>
                    </AgxRow>
                  )}
                </TeamMemberForm.CustomField>
              )}
              {values.useLicenseeDetails && canViewLicenseeDetails && (
                <>
                  <TeamMemberForm.Field
                    name="agentCompanyDetails.licenseeName"
                    Input={AgxFormTextField}
                    type="text"
                    label="Licensee name"
                    hideOptionalText
                    stretch
                  />
                  <AgxRow gap="spacing-3xl" fill>
                    <TeamMemberForm.Field
                      name="agentCompanyDetails.abn"
                      Input={AgxFormTextField}
                      type="abn"
                      label="ABN"
                      hideOptionalText
                      stretch
                    />
                    <TeamMemberForm.Field
                      name="agentCompanyDetails.acn"
                      Input={AgxFormTextField}
                      type="acn"
                      label="ACN"
                      hideOptionalText
                      stretch
                    />
                  </AgxRow>
                  <AgxRow gap="spacing-3xl" fill>
                    <TeamMemberForm.Field
                      name="licenceNumber"
                      Input={AgxFormTextField}
                      type="text"
                      label="Licence Number"
                      hideOptionalText
                      stretch
                    />
                    <TeamMemberForm.Field
                      name="expiryDate"
                      Input={AgxFormDateField}
                      variant="compact"
                      label="Expiry Date"
                      stretch
                    />
                  </AgxRow>
                </>
              )}
              <AgxColumn gap="spacing-lg" fill>
                <AgxTypography id="extrasText" type="body_ui" size="small">
                  Extras
                </AgxTypography>
                <AgxDivider variant="horizontal" />
              </AgxColumn>
              <TeamMemberForm.CustomField
                name="instructionsConditions"
                focus={() => document.getElementById('instructionsConditions')?.focus()}
              >
                {(field) => (
                  <AgxTextArea
                    id="instructionsConditions"
                    label="Reassignment Clause"
                    defaultValue={field.value}
                    disabled={field.disabled}
                    error={field.errorText}
                    onInputValueChange={({ value }) => {
                      field.onChange(value);
                      field.onBlur();
                    }}
                    rows={6}
                    maxLength={1100}
                    hideOptionalText
                    stretch
                  />
                )}
              </TeamMemberForm.CustomField>
              {isExistingMember && (
                <AgxColumn gap="spacing-lg">
                  <AgxTypography id="emailInvitation" type="body_ui" size="small">
                    Email Invitation
                  </AgxTypography>
                  <AgxButton
                    id="sendInvitationEmailButton"
                    text={
                      sendInvitationEmailPending
                        ? 'Sending...'
                        : emailInviteSent
                          ? 'Resend Invite'
                          : 'Send Invite'
                    }
                    size="medium"
                    variant="primary"
                    disabled={sendInvitationEmailPending || emailInviteSent || isSaving}
                    onClick={() => sendInvitationEmail({ agentId: values.id })}
                  />
                </AgxColumn>
              )}
            </Panel>
          </AgxSpacer>
          {showRebates && (
            <AgxSpacer fill className="agx-team-member-rebates">
              {!agencyRebates && (
                <AgxTypography
                  type={listingError ? 'form_error' : 'body_ui'}
                  role={listingError ? 'alert' : undefined}
                >
                  {listingError
                    ? 'Unable to load agency rebates. Reload the page to edit rebates. Profile changes can still be saved.'
                    : 'Loading agency rebates…'}
                </AgxTypography>
              )}
              {agencyRebates && (
                <AgentRebates form={form} agencyRebates={agencyRebates} licences={licences} />
              )}
            </AgxSpacer>
          )}
        </TeamMemberForm.Root>
        {isExistingMember && (
          <AgxSpacer className="agx-team-member-profile">
            <AgentCrmRegistration agencyId={agencyId} agentId={values.id} />
          </AgxSpacer>
        )}
      </AgxSpacer>
      <DeleteConfirmation
        name={`${values.firstName} ${values.lastName}`.trim()}
        title="Really delete user?"
        lineOne="Are you sure you want to delete user"
        lineTwo="This cannot be undone."
        primaryActionTitle="Permanently delete user"
        secondaryActionTitle="No, cancel"
        showPopUp={showDeleteConfirmation}
        disabled={softDeleteAgentPending}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={() => {
          if (!softDeleteAgentPending) softDeleteAgent({ agentId: values.id });
        }}
      />
      <Popup
        title=""
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          if (!isEdit && values.id)
            navigate(`../team-members/edit/${values.id}`, { replace: true });
        }}
        actionButtons={[]}
        size={{ widthInPX: 600 }}
        centered
        actionButtonsCentered
        autoDismissMs={SUCCESS_MODAL_AUTO_DISMISS_MS}
      >
        <AgxColumn gap="spacing-xl" alignItems="alignCenter">
          <Icon_CheckmarkCircleOutlineBig />
          <AgxTypography id="team-member-success-message" type="body_ui" size="medium">
            {successMessage}
          </AgxTypography>
        </AgxColumn>
      </Popup>
    </AgxColumn>
  );
};
export default AddOrEditTeamMemberPage;
